import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {MeshViewTargetType} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshViewTargetType';
import {ResizeEvent, ResizePhase, Resizer} from '@mintdata-wsp/wsp/ui/components/resizable/Resizer';
import {IStyles, Styles} from '@mintdata/mint-kit/impl/util/Styles';
import * as classnames from 'classnames/bind';
import {action, computed} from 'mobx';
import {observer, inject} from 'mobx-react';
import * as React from 'react';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';

const meshRowHeaderStyles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/Mesh.less');
const cx = classnames.bind(meshRowHeaderStyles);

interface IMeshRowHeaderProps
{
    ctx?:IWSPMeshContext;
    rowIndex:number;
    theme?:IStyles;
    isNested:boolean;
}

@inject('ctx')
@observer
export class MeshRowHeader extends React.Component<IMeshRowHeaderProps>
{
    public componentWillMount():void
    {
        const {theme} = this.props;

        this._initStyles(theme);
    }

    public componentWillReceiveProps(newProps:IMeshRowHeaderProps):void
    {
        const {theme} = this.props;

        if( theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    private _initStyles(theme:IStyles):void
    {
        this.setState({
            styles: Styles.mergeStyles(meshRowHeaderStyles, theme || {})
        });
    }

    @computed
    private get _height():number
    {
        const {ctx, rowIndex} = this.props;
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {rowHeights, details: {resizingRowIndex, resizingRowHeight}} = meshState;

        return resizingRowIndex === rowIndex && resizingRowHeight != null
            ? resizingRowHeight
            : rowHeights[rowIndex];
    }

    @computed
    private get _style():React.CSSProperties
    {
        return {
            height: `${this._height}px`
        };
    }

    @computed
    private get _isSelected():boolean
    {
        const {ctx, rowIndex} = this.props;
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {
            selectedRange:
                {
                    from: {
                        rowIndex: selectedRowFrom
                    },
                    to: {
                        rowIndex: selectedRowTo
                    }
                }
        } = meshState.details;

        return (rowIndex >= selectedRowFrom && rowIndex <= selectedRowTo)
            || (selectedRowFrom == null && selectedRowTo == null);
    }

    @action.bound
    private _onResize(event:ResizeEvent):void
    {
        const {ctx, rowIndex} = this.props;
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {rowHeights, details: {resizingRowHeight}} = meshState;

        const newRowHeight:number = (resizingRowHeight != null
            ? resizingRowHeight
            : rowHeights[rowIndex]) + event.deltaY;

        if( event.phase === ResizePhase.Stop )
        {
            meshState.setRowHeight(rowIndex, Math.max(10, newRowHeight));
            meshState.resetResizingRow();
        }
        else
            meshState.setResizingRow(rowIndex, Math.max(10, newRowHeight));
    }

    @action.bound
    private _onResizerDblClick(event:React.MouseEvent<HTMLDivElement>):void
    {
        const {ctx, rowIndex} = this.props;

        event.preventDefault();
        event.stopPropagation();

        setTimeout(() => controllers.ui.spreadsheets.mesh.calcRowHeightDblClick(ctx, rowIndex));
    }

    public render():JSX.Element
    {
        const {ctx, rowIndex, isNested} = this.props;
        const meshState:MeshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        const {size: {numRows}} = meshState;

        if( rowIndex >= numRows )
            return null;

        return <div
            data-type={MeshViewTargetType.RowHeader}
            key={rowIndex}
            style={this._style}
            className={cx(meshRowHeaderStyles.tableHeader, 'row', {isSelected: this._isSelected}, {isNested})}
        >
            <span className={meshRowHeaderStyles.tableRowHeaderText}>
                {rowIndex + 1}
            </span>
            <Resizer
                isVisible={true}
                isVertical={false}
                className={meshRowHeaderStyles.tableRowHeaderResizer}
                onResize={this._onResize}
                onDblClick={this._onResizerDblClick}
            />
        </div>;
    }
}
